import { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Shared markdown component configuration with semantic color system
 *
 * Used by both Production and Debug UIs for consistent markdown rendering.
 * Applies Autumn's design system (semantic colors, typography scales).
 *
 * @see https://github.com/remarkjs/react-markdown
 */

/**
 * Remark plugins for GitHub Flavored Markdown support
 * Enables tables, strikethrough, task lists, and other GFM features
 */
export const remarkPlugins = [remarkGfm];

/**
 * Markdown nodes inherit container typography by default (body font,
 * 13.5px, line 1.55, ink color set on the bubble in AssistantMessage).
 * We only set the things that *differ* from the parent — weights for
 * strong / headings, mono font for code, kit accent for links, list
 * tweaks, table chrome. This keeps streaming text visually stable
 * with the user bubble across all viewports.
 */
export const markdownComponents: Components = {
  // Paragraph — inherits font/size from parent bubble
  p: ({ ...props }) => (
    <p className="mb-2 last:mb-0" {...props} />
  ),

  // Inline code — kit mono on cream-soft tint with hairline rule
  code: ({ className, ...props }) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <code
        className="block p-2.5 rounded-md overflow-x-auto my-2"
        style={{
          background: 'var(--d-tint-page)',
          border: '1px solid var(--d-rule)',
          color: 'var(--d-ink)',
          fontFamily: 'var(--d-font-mono)',
          fontSize: 12,
          lineHeight: 1.5,
        }}
        {...props}
      />
    ) : (
      <code
        className="px-1 py-0.5 rounded-[4px]"
        style={{
          background: 'var(--d-tint-page)',
          border: '1px solid var(--d-rule-soft)',
          color: 'var(--d-ink)',
          fontFamily: 'var(--d-font-mono)',
          fontSize: 12,
        }}
        {...props}
      />
    );
  },

  // Lists — disc/decimal, inherit body font, breathing-room between items
  ul: ({ ...props }) => (
    <ul className="list-disc pl-5 mb-2 space-y-1 marker:text-[var(--d-ink-ghost)]" {...props} />
  ),
  ol: ({ ...props }) => (
    <ol className="list-decimal pl-5 mb-2 space-y-1 marker:text-[var(--d-ink-ghost)]" {...props} />
  ),
  li: ({ ...props }) => (
    <li className="leading-relaxed" {...props} />
  ),

  // Links — kit accent-deep with underline-offset for legibility
  a: ({ ...props }) => (
    <a
      className="underline underline-offset-2 transition-colors"
      style={{ color: 'var(--d-accent-deep)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--d-ink)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--d-accent-deep)')}
      {...props}
    />
  ),

  // Strong — Geist 600 for a touch more weight contrast vs body
  strong: ({ ...props }) => (
    <strong
      style={{
        fontFamily: 'var(--d-font-sans)',
        fontWeight: 600,
        color: 'var(--d-ink)',
        letterSpacing: '-0.005em',
      }}
      {...props}
    />
  ),

  // Emphasis — slight ink-muted shift to read as a soft aside
  em: ({ ...props }) => (
    <em className="italic" style={{ color: 'var(--d-ink-muted)' }} {...props} />
  ),

  // Headings — Geist, chat-scaled (don't dwarf the body)
  h1: ({ ...props }) => (
    <h1
      className="mb-2 mt-3 first:mt-0"
      style={{
        fontFamily: 'var(--d-font-sans)',
        fontSize: 16,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: 'var(--d-ink)',
      }}
      {...props}
    />
  ),
  h2: ({ ...props }) => (
    <h2
      className="mb-2 mt-2.5 first:mt-0"
      style={{
        fontFamily: 'var(--d-font-sans)',
        fontSize: 14.5,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        color: 'var(--d-ink)',
      }}
      {...props}
    />
  ),
  h3: ({ ...props }) => (
    <h3
      className="mb-1 mt-2 first:mt-0"
      style={{
        fontFamily: 'var(--d-font-sans)',
        fontSize: 13.5,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        color: 'var(--d-ink)',
      }}
      {...props}
    />
  ),

  // Tables — kit hairlines + cream-soft head, mono for cell values
  table: ({ ...props }) => (
    <table className="w-full border-collapse my-3" {...props} />
  ),
  thead: ({ ...props }) => (
    <thead
      style={{
        background: 'var(--d-cream-soft)',
        borderBottom: '1px solid var(--d-rule)',
      }}
      {...props}
    />
  ),
  tbody: ({ ...props }) => <tbody {...props} />,
  tr: ({ ...props }) => (
    <tr style={{ borderBottom: '1px solid var(--d-rule-soft)' }} {...props} />
  ),
  th: ({ ...props }) => (
    <th
      className="text-left py-2 px-3"
      style={{
        fontFamily: 'var(--d-font-mono)',
        fontSize: 10.5,
        fontWeight: 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--d-ink-muted)',
      }}
      {...props}
    />
  ),
  td: ({ ...props }) => (
    <td
      className="py-2 px-3"
      style={{
        fontFamily: 'var(--d-font-body)',
        fontSize: 12.5,
        color: 'var(--d-ink)',
      }}
      {...props}
    />
  ),

  // Block quotes — left-rule accent
  blockquote: ({ ...props }) => (
    <blockquote
      className="my-2 pl-3"
      style={{
        borderLeft: '2px solid var(--d-accent)',
        color: 'var(--d-ink-muted)',
        fontStyle: 'italic',
      }}
      {...props}
    />
  ),

  // Horizontal rule — kit hairline
  hr: ({ ...props }) => (
    <hr
      className="my-3"
      style={{
        border: 0,
        borderTop: '1px solid var(--d-rule)',
      }}
      {...props}
    />
  ),
};
